<script>
import axios from 'axios';
import cbLogo from 'assets/images/cb_logo.png';

export default {
  name: 'ManagementPanel',
  data() {
    return {
      cbLogo,
      applications: [],
      searchQuery: '',
      dateFilter: '',
      statusFilter: '',
      statusOptions: [
        { value: 'new', label: 'Novo' },
        { value: 'in_review', label: 'Em Análise' },
        { value: 'interviewed', label: 'Entrevistado' },
        { value: 'approved', label: 'Aprovado' },
        { value: 'rejected', label: 'Reprovado' },
      ],
      timeoutId: null,
      selectedApplication: null,
      printingApplication: null,
      saveError: '',
      saveSuccess: '',
      originalDocumentTitle: null,
    };
  },
  mounted() {
    this.originalDocumentTitle = document.title;
    document.title = 'Painel de Candidaturas — RH Coco Bambu';
    this.fetchApplications();
  },
  beforeUnmount() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    if (this.originalDocumentTitle != null) {
      document.title = this.originalDocumentTitle;
    }
  },
  methods: {
    formatDate(dateStr) {
      if (!dateStr) return '-';
      const [year, month, day] = dateStr.split('-');
      return `${day}/${month}/${year}`;
    },
    async fetchApplications() {
      const token = this.$route.params.token;
      try {
        const response = await axios.get(
          `/api/v1/public/recruitment/${token}/applications`,
          {
            params: {
              search: this.searchQuery,
              interview_date: this.dateFilter,
              status: this.statusFilter || undefined,
            },
          }
        );
        this.applications = response.data.data;
      } catch (error) {
        this.saveError = 'Não foi possível carregar as candidaturas.';
      }
    },
    debouncedFetch() {
      clearTimeout(this.timeoutId);
      this.timeoutId = setTimeout(() => {
        this.fetchApplications();
      }, 300);
    },
    checkboxGroup(expected, actualVal) {
      if (!actualVal) actualVal = '';
      let result = '';
      expected.forEach(v => {
        const isChecked = Array.isArray(actualVal)
          ? actualVal.includes(v)
          : actualVal === v;
        result += `<span style="white-space:nowrap; margin-right:12px;">${isChecked ? '( X ) ' : '( &nbsp;&nbsp; ) '}${this.escapeHtml(v)}</span>`;
      });
      return result;
    },
    escapeHtml(str) {
      if (str == null) return '';
      return String(str).replace(/[&<>"']/g, m => {
        return {
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#39;',
        }[m];
      });
    },
    statusLabel(status) {
      const opt = this.statusOptions.find(item => item.value === status);
      return opt ? opt.label : status;
    },
    async saveInternalNotes() {
      if (!this.selectedApplication) return;

      const token = this.$route.params.token;
      const interviewDate = this.selectedApplication.interview_date || null;
      this.saveError = '';
      this.saveSuccess = '';
      try {
        await axios.patch(
          `/api/v1/public/recruitment/${token}/applications/${this.selectedApplication.id}`,
          {
            job_application: {
              status: this.selectedApplication.status,
              interview_date: interviewDate,
              form_data: this.selectedApplication.form_data,
            },
          }
        );
        const listed = this.applications.find(
          app => app.id === this.selectedApplication.id
        );
        if (listed) {
          listed.status = this.selectedApplication.status;
          listed.interview_date = interviewDate;
          listed.form_data = { ...this.selectedApplication.form_data };
        }
        this.saveSuccess = 'Alterações salvas com sucesso!';
      } catch (error) {
        this.saveError = 'Não foi possível salvar. Tente novamente.';
      }
    },
    viewDetails(app) {
      // Clone it to avoid reactive pollution if canceled
      const clone = JSON.parse(JSON.stringify(app));
      clone.interview_date = clone.interview_date || '';
      this.selectedApplication = clone;
    },
    closeDetails() {
      this.selectedApplication = null;
      this.fetchApplications(); // refresh in case it changed
    },
    // vite_rails links CSS as media="screen", so browsers skip those sheets when printing.
    // Only for this RH print flow, temporarily allow them during window.print().
    enableStylesheetsForPrint() {
      const links = Array.from(
        document.querySelectorAll('link[rel="stylesheet"][media="screen"]')
      );
      links.forEach(link => {
        link.dataset.recruitmentPrintMedia = 'screen';
        link.media = 'all';
      });
      return links;
    },
    restoreStylesheetsAfterPrint(links) {
      links.forEach(link => {
        if (link.dataset.recruitmentPrintMedia === 'screen') {
          link.media = 'screen';
          delete link.dataset.recruitmentPrintMedia;
        }
      });
    },
    doPrint(app) {
      this.printingApplication = app;
      const originalTitle = document.title;
      const safeName = app.name
        ? app.name.replace(/[^a-zA-Z0-9 ]/g, '')
        : 'Candidato';
      const safeJob =
        app.form_data && app.form_data.job_option_1
          ? app.form_data.job_option_1.replace(/[^a-zA-Z0-9 ]/g, '')
          : 'Vaga';
      document.title = `${safeName} - ${safeJob}`;

      const printStylesheets = this.enableStylesheetsForPrint();

      let cleaned = false;
      const cleanup = () => {
        if (cleaned) return;
        cleaned = true;
        this.restoreStylesheetsAfterPrint(printStylesheets);
        document.title = originalTitle;
        this.printingApplication = null;
        window.removeEventListener('afterprint', cleanup);
      };
      window.addEventListener('afterprint', cleanup);

      this.$nextTick(() => {
        // Wait for ficha DOM + images before opening the print dialog.
        setTimeout(() => {
          window.print();
          // Fallback if afterprint is not fired by the browser.
          setTimeout(cleanup, 1000);
        }, 300);
      });
    },
    printCard() {
      this.doPrint(this.selectedApplication);
    },
  },
};
</script>

<template>
  <div class="management-panel helena-theme helena-page-bg min-h-screen p-8">
    <div class="max-w-6xl mx-auto">
      <header
        class="recruitment-no-print flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6 p-6 rounded-lg shadow-sm border border-slate-200 bg-white"
      >
        <div class="flex items-center gap-4 shrink-0">
          <div class="shrink-0">
            <img
              :src="cbLogo"
              alt="Coco Bambu"
              class="w-12 h-12 object-contain"
            />
          </div>
          <div>
            <div
              class="text-primary-500 text-xs font-bold uppercase tracking-widest"
            >
              RH Coco Bambu
            </div>
            <h1 class="text-slate-900 text-2xl font-semibold mt-1">
              Painel de Candidaturas
            </h1>
          </div>
        </div>
        <div class="panel-filters">
          <select
            v-model="statusFilter"
            class="status-select reset-base"
            aria-label="Filtrar por status"
            @change="fetchApplications"
          >
            <option value="">Todos os status</option>
            <option
              v-for="opt in statusOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
          <input
            v-model="dateFilter"
            type="date"
            class="panel-filter-input panel-filter-input--date reset-base"
            @change="fetchApplications"
          />
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Buscar por Nome ou CPF..."
            class="panel-filter-input panel-filter-input--search reset-base"
            @input="debouncedFetch"
          />
        </div>
      </header>

      <div
        class="recruitment-no-print bg-white shadow rounded-lg overflow-hidden"
      >
        <table class="min-w-full divide-y divide-slate-200">
          <thead class="bg-slate-50">
            <tr>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider"
              >
                Data da Entrevista
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider"
              >
                Nome
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider"
              >
                CPF
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider"
              >
                Status
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider"
              >
                Ações
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-slate-200">
            <tr v-for="app in applications" :key="app.id">
              <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                {{ formatDate(app.interview_date) }}
              </td>
              <td
                class="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900"
              >
                {{ app.name }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                {{ app.cpf }}
              </td>
              <td
                class="px-6 py-4 whitespace-nowrap text-sm text-slate-700 font-medium"
              >
                {{ statusLabel(app.status) }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <button
                  class="text-primary-600 hover:text-primary-800 font-semibold"
                  @click="viewDetails(app)"
                >
                  Ver Ficha
                </button>
              </td>
            </tr>
          </tbody>
        </table>

        <div
          v-if="applications.length === 0"
          class="p-6 text-center text-slate-500"
        >
          Nenhuma candidatura encontrada.
        </div>
      </div>

      <!-- Modal Ficha de Impressão -->
      <div
        v-if="selectedApplication"
        class="recruitment-no-print fixed inset-0 z-10 overflow-y-auto"
        aria-labelledby="modal-title"
        role="dialog"
        aria-modal="true"
      >
        <div
          class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
        >
          <div
            class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            aria-hidden="true"
            @click="closeDetails"
          />
          <span
            class="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
            >&#8203;</span
          >
          <div
            class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full"
          >
            <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <h3
                id="modal-title"
                class="text-lg leading-6 font-medium text-gray-900 mb-4"
              >
                Detalhes da Candidatura
              </h3>
              <div class="grid grid-cols-2 gap-4 text-sm">
                <div><strong>Nome:</strong> {{ selectedApplication.name }}</div>
                <div>
                  <strong>E-mail:</strong> {{ selectedApplication.email }}
                </div>
                <div><strong>CPF:</strong> {{ selectedApplication.cpf }}</div>
                <div>
                  <strong>Telefone:</strong>
                  {{ selectedApplication.form_data.phone }}
                </div>
                <div>
                  <strong>Vaga Desejada:</strong>
                  {{ selectedApplication.form_data.job_option_1 }}
                </div>
                <div class="col-span-2">
                  <strong>Experiência:</strong>
                  {{ selectedApplication.form_data.experience_description }}
                </div>
              </div>

              <hr class="my-4" />
              <h4 class="font-medium text-gray-900 mb-2">Uso Interno (RH)</h4>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <div>
                  <label
                    class="block text-sm font-medium text-slate-700 mb-1"
                    for="ficha-status"
                    >Status</label
                  >
                  <select
                    id="ficha-status"
                    v-model="selectedApplication.status"
                    class="status-select reset-base"
                    aria-label="Status da candidatura"
                  >
                    <option
                      v-for="opt in statusOptions"
                      :key="opt.value"
                      :value="opt.value"
                    >
                      {{ opt.label }}
                    </option>
                  </select>
                </div>
                <div>
                  <label
                    class="block text-sm font-medium text-slate-700 mb-1"
                    for="ficha-interview-date"
                    >Data da entrevista</label
                  >
                  <input
                    id="ficha-interview-date"
                    v-model="selectedApplication.interview_date"
                    type="date"
                    class="panel-filter-input panel-filter-input--date reset-base"
                  />
                </div>
              </div>
              <textarea
                v-model="selectedApplication.form_data.internal_notes"
                rows="4"
                class="w-full border-slate-300 rounded-md shadow-sm sm:text-sm p-2 border"
                placeholder="Anotações da entrevista..."
              />
            </div>
            <div class="bg-gray-50 px-4 py-3 sm:px-6 flex justify-between">
              <div class="flex flex-col gap-1">
                <button
                  type="button"
                  class="btn-primary mr-2"
                  @click="saveInternalNotes"
                >
                  Salvar
                </button>
                <p v-if="saveSuccess" class="text-sm text-green-600 m-0">
                  {{ saveSuccess }}
                </p>
                <p v-if="saveError" class="text-sm text-red-600 m-0">
                  {{ saveError }}
                </p>
              </div>
              <div class="space-x-2">
                <button
                  type="button"
                  class="bg-primary-500 text-white rounded-md border border-transparent px-4 py-2 text-base font-medium shadow-sm hover:bg-primary-600 focus:outline-none sm:text-sm"
                  @click="printCard"
                >
                  Imprimir (PDF)
                </button>
                <button
                  type="button"
                  class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  @click="closeDetails"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Ficha apenas visível para impressão -->
      <div
        v-if="printingApplication"
        class="recruitment-print-only ficha-wrapper"
      >
        <!-- HEADER -->
        <table class="ficha-outer">
          <tr>
            <td class="ficha-logo-cell">
              <img :src="cbLogo" style="max-height: 40px" />
            </td>
            <td class="ficha-title-cell">FICHA DE SOLICITAÇÃO DE EMPREGO</td>
            <td class="ficha-num-cell">
              <span class="ficha-label">Nº Candidato:</span><br />{{
                printingApplication.id
              }}
            </td>
          </tr>
        </table>

        <!-- DADOS PESSOAIS -->
        <div class="ficha-section-bar">Dados Pessoais</div>
        <table class="ficha-outer">
          <tr>
            <td colspan="4" class="ficha-row">
              <span class="ficha-label">Nome Completo:</span>
              {{ printingApplication.name }}
            </td>
          </tr>
          <tr>
            <td colspan="2" class="ficha-row">
              <span class="ficha-label">Telefone para contato com DDD:</span>
              {{ printingApplication.form_data.phone }}
            </td>
            <td colspan="2" class="ficha-row">
              <span class="ficha-label">Recado:</span>
              {{
                printingApplication.form_data['Telefone para recado com DDD']
              }}
            </td>
          </tr>
          <tr>
            <td colspan="4" class="ficha-row">
              <span class="ficha-label">Redes Sociais:</span><br />
              <span class="ficha-label">Facebook:</span>
              {{ printingApplication.form_data['Facebook'] }}<br />
              <span class="ficha-label">Instagram:</span>
              {{ printingApplication.form_data['Instagram'] }}<br />
              <span class="ficha-label">Linkedin:</span>
              {{ printingApplication.form_data['LinkedIn'] }}
            </td>
          </tr>
          <tr>
            <td colspan="4" class="ficha-row">
              <span class="ficha-label">Email:</span>
              {{ printingApplication.email }}
            </td>
          </tr>
          <tr>
            <td colspan="4" class="ficha-row">
              <span class="ficha-label">Sexo:</span>
              <span
                v-html="
                  checkboxGroup(
                    ['Masculino', 'Feminino'],
                    printingApplication.form_data['Sexo']
                  )
                "
              />
            </td>
          </tr>
          <tr>
            <td class="ficha-row" style="width: 30%">
              <span class="ficha-label">CPF:</span>
              {{ printingApplication.cpf }}
            </td>
            <td class="ficha-row" style="width: 20%">
              <span class="ficha-label">RG:</span>
              {{ printingApplication.form_data['RG'] }}
            </td>
            <td colspan="2" class="ficha-row">
              <span class="ficha-label">Data de Nascimento:</span>
              {{ printingApplication.form_data['Data de nascimento'] }}
            </td>
          </tr>
          <tr>
            <td colspan="4" class="ficha-row">
              <span class="ficha-label">Naturalidade:</span>
              <span
                v-html="
                  checkboxGroup(
                    ['Brasileira', 'Outra'],
                    printingApplication.form_data['Naturalidade']
                  )
                "
              />
            </td>
          </tr>
          <tr>
            <td colspan="4" class="ficha-row">
              <span class="ficha-label">Endereço:</span>
              {{ printingApplication.form_data['Endereço — rua/avenida'] }}
              &nbsp;&nbsp; <span class="ficha-label">Nº:</span>
              {{ printingApplication.form_data['Número'] }} &nbsp;&nbsp;
              <span class="ficha-label">Bairro:</span>
              {{ printingApplication.form_data['Bairro'] }} &nbsp;&nbsp;
              <span class="ficha-label">Cidade:</span>
              {{ printingApplication.form_data['Cidade/UF'] }} &nbsp;&nbsp;
              <span class="ficha-label">CEP:</span>
              {{ printingApplication.form_data['CEP'] }}
            </td>
          </tr>
          <tr>
            <td colspan="4" class="ficha-row">
              <span class="ficha-label">Local de Nascimento:</span>
              {{
                printingApplication.form_data[
                  'Local de nascimento — cidade/UF ou país'
                ]
              }}
            </td>
          </tr>
          <tr>
            <td colspan="2" class="ficha-row">
              <span class="ficha-label"
                >Tempo de deslocamento até a empresa:</span
              >
              {{
                printingApplication.form_data[
                  'Tempo estimado de deslocamento até a empresa'
                ]
              }}
            </td>
            <td colspan="2" class="ficha-row">
              <span class="ficha-label"
                >Valor total das conduções (Ida e Volta):</span
              >
              {{
                printingApplication.form_data[
                  'Valor total diário das conduções — ida e volta'
                ]
              }}
            </td>
          </tr>
          <tr>
            <td colspan="4" class="ficha-row">
              <span class="ficha-label">Você possui Alergia?</span>
              {{
                printingApplication.form_data[
                  'Você possui alguma alergia ou condição que deva ser considerada no ambiente de trabalho?'
                ]
              }}
            </td>
          </tr>
        </table>

        <!-- SITUAÇÃO FAMILIAR -->
        <div class="ficha-section-bar">Situação Familiar</div>
        <table class="ficha-outer">
          <tr>
            <td class="ficha-row" style="width: 55%">
              <span class="ficha-label">Nome da Mãe:</span>
              {{ printingApplication.form_data['Nome da Mãe'] }}
            </td>
            <td class="ficha-row">
              <span class="ficha-label">Cidade de Nascimento:</span>
              {{ printingApplication.form_data['Cidade de Nascimento da Mãe'] }}
            </td>
          </tr>
          <tr>
            <td class="ficha-row">
              <span class="ficha-label">Nome do Pai:</span>
              {{ printingApplication.form_data['Nome do Pai'] }}
            </td>
            <td class="ficha-row">
              <span class="ficha-label">Cidade de Nascimento:</span>
              {{ printingApplication.form_data['Cidade de Nascimento do Pai'] }}
            </td>
          </tr>
          <tr>
            <td colspan="2" class="ficha-row">
              <span class="ficha-label">Mora com quem:</span>
              {{ printingApplication.form_data['Mora com quem'] }}
            </td>
          </tr>
        </table>

        <!-- GRAU DE ESCOLARIDADE -->
        <div class="ficha-section-bar">Grau de escolaridade</div>
        <table class="ficha-outer">
          <tr>
            <td class="ficha-row">
              <div
                v-html="
                  checkboxGroup(
                    ['Analfabeto'],
                    printingApplication.form_data['Grau de escolaridade']
                  )
                "
              />
              <div
                v-html="
                  checkboxGroup(
                    ['Ensino Fundamental Incompleto (Até 4ª Série)'],
                    printingApplication.form_data['Grau de escolaridade']
                  )
                "
              />
              <div
                v-html="
                  checkboxGroup(
                    ['Ensino Fundamental Incompleto'],
                    printingApplication.form_data['Grau de escolaridade']
                  )
                "
              />
              <div
                v-html="
                  checkboxGroup(
                    ['Ensino Fundamental Completo'],
                    printingApplication.form_data['Grau de escolaridade']
                  )
                "
              />
              <div
                v-html="
                  checkboxGroup(
                    ['Ensino Médio Incompleto'],
                    printingApplication.form_data['Grau de escolaridade']
                  )
                "
              />
              <div
                v-html="
                  checkboxGroup(
                    ['Ensino Médio Completo'],
                    printingApplication.form_data['Grau de escolaridade']
                  )
                "
              />
              <div
                v-html="
                  checkboxGroup(
                    ['Educação Superior Incompleto'],
                    printingApplication.form_data['Grau de escolaridade']
                  )
                "
              />
              <div
                v-html="
                  checkboxGroup(
                    ['Educação Superior Completo'],
                    printingApplication.form_data['Grau de escolaridade']
                  )
                "
              />
            </td>
          </tr>
          <tr>
            <td class="ficha-row">
              <span class="ficha-label">Estuda atualmente:</span>
              <span
                v-html="
                  checkboxGroup(
                    ['Sim', 'Não'],
                    printingApplication.form_data['Estuda atualmente?']
                  )
                "
              />
            </td>
          </tr>
          <tr
            v-if="
              printingApplication.form_data['Estuda atualmente?'] &&
              printingApplication.form_data['Estuda atualmente?'].includes(
                'Sim'
              )
            "
          >
            <td class="ficha-row">
              <span class="ficha-label">O quê?</span>
              {{
                printingApplication.form_data['Se estuda, o que está cursando?']
              }}
              &nbsp;&nbsp;&nbsp;&nbsp; <span class="ficha-label">Onde?</span>
              {{ printingApplication.form_data['Se estuda, onde?'] }}
            </td>
          </tr>
          <tr>
            <td class="ficha-row">
              <span class="ficha-label">Experiência na área:</span>
              <span
                v-html="
                  checkboxGroup(
                    ['Sim', 'Não'],
                    printingApplication.form_data[
                      'Você tem experiência na vaga desejada?'
                    ]
                  )
                "
              />
            </td>
          </tr>
          <tr
            v-if="
              printingApplication.form_data[
                'Você tem experiência na vaga desejada?'
              ] &&
              printingApplication.form_data[
                'Você tem experiência na vaga desejada?'
              ].includes('Sim')
            "
          >
            <td class="ficha-row">
              <span class="ficha-label">Se sim, qual?</span>
              {{ printingApplication.form_data.experience_description }}
            </td>
          </tr>
          <tr>
            <td class="ficha-row">
              <span class="ficha-label">Idiomas:</span>
              <span
                v-html="
                  checkboxGroup(
                    ['Inglês', 'Espanhol'],
                    printingApplication.form_data['Idiomas']
                  )
                "
              />
              ( &nbsp; ) Outro: _________ &nbsp;&nbsp;&nbsp;&nbsp;
              <span class="ficha-label">Conhecimentos em informática:</span>
              {{
                printingApplication.form_data['Conhecimentos em informática']
              }}
            </td>
          </tr>
          <tr>
            <td class="ficha-row">
              <span class="ficha-label">Outros cursos:</span>
              {{
                printingApplication.form_data['Outros cursos e certificações']
              }}
            </td>
          </tr>
        </table>

        <!-- OUTRAS INFORMAÇÕES -->
        <div class="ficha-section-bar">Outras Informações</div>
        <table class="ficha-outer">
          <tr>
            <td class="ficha-row" style="width: 35%">
              <span class="ficha-label">Número do calçado:</span>
              {{ printingApplication.form_data['Número do calçado'] }}
            </td>
            <td class="ficha-row">
              <span class="ficha-label">Tamanho da Blusa:</span>
              <span
                v-html="
                  checkboxGroup(
                    ['PP', 'P', 'M', 'G', 'GG'],
                    printingApplication.form_data['Tamanho da blusa']
                  )
                "
              />
            </td>
          </tr>
        </table>

        <!-- SOBRE A POSSIBILIDADE DE CONTRATAÇÃO -->
        <div class="ficha-section-bar">
          Sobre a Possibilidade de Contratação
        </div>
        <table class="ficha-outer">
          <tr>
            <td class="ficha-row" style="width: 33%">
              <span class="ficha-label">Pretensão Salarial:</span> R$
              {{ printingApplication.form_data['Pretensão salarial'] }}
            </td>
            <td class="ficha-row" style="width: 34%">
              <span class="ficha-label">Último Cargo:</span>
              {{ printingApplication.form_data['Último cargo'] }}
            </td>
            <td class="ficha-row" style="width: 33%">
              <span class="ficha-label">Último Salário:</span> R$
              {{ printingApplication.form_data['Último salário'] }}
            </td>
          </tr>
          <tr>
            <td colspan="3" class="ficha-row">
              <span class="ficha-label">Disponibilidade de tempo:</span>
              <div
                v-html="
                  checkboxGroup(
                    ['Abertura 08:00h - 16:20h'],
                    printingApplication.form_data['Disponibilidade de horário']
                  )
                "
              />
              <div
                v-html="
                  checkboxGroup(
                    ['Intercalado 11:00h - 15:00h/19:00h - 22:20h'],
                    printingApplication.form_data['Disponibilidade de horário']
                  )
                "
              />
              <div
                v-html="
                  checkboxGroup(
                    ['Fechamento 16:00h - 00:20h'],
                    printingApplication.form_data['Disponibilidade de horário']
                  )
                "
              />
            </td>
          </tr>
          <tr>
            <td colspan="3" class="ficha-row">
              <span class="ficha-label"
                >Disponibilidade para trabalhar escala 6x1:</span
              >
              <span
                v-html="
                  checkboxGroup(
                    ['Sim', 'Não'],
                    printingApplication.form_data[
                      'Tem disponibilidade para trabalhar em escala 6x1?'
                    ]
                  )
                "
              />
            </td>
          </tr>
          <tr>
            <td colspan="3" class="ficha-row">
              <span class="ficha-label"
                >Aceita trabalhar sábados, domingos e feriados?</span
              >
              <span
                v-html="
                  checkboxGroup(
                    ['Sim', 'Não'],
                    printingApplication.form_data[
                      'Aceita trabalhar aos sábados, domingos e feriados?'
                    ]
                  )
                "
              />
            </td>
          </tr>
          <tr>
            <td colspan="3" class="ficha-row">
              <span class="ficha-label">Disponibilidade para viagens?</span>
              <span
                v-html="
                  checkboxGroup(
                    ['Sim', 'Não'],
                    printingApplication.form_data[
                      'Tem disponibilidade para viagens?'
                    ]
                  )
                "
              />
            </td>
          </tr>
          <tr>
            <td colspan="3" class="ficha-row">
              <span class="ficha-label">Disponibilidade para mudança?</span>
              <span
                v-html="
                  checkboxGroup(
                    ['Sim', 'Não'],
                    printingApplication.form_data[
                      'Tem disponibilidade para mudança?'
                    ]
                  )
                "
              />
            </td>
          </tr>
          <tr>
            <td colspan="3" class="ficha-row">
              <span class="ficha-label"
                >Descreva o seu deslocamento de casa para a unidade do Coco
                Bambu?</span
              >
              {{
                printingApplication.form_data[
                  'Descreva seu deslocamento de casa até o Coco Bambu Uberlândia.'
                ]
              }}
            </td>
          </tr>
        </table>

        <!-- INFORMAÇÕES PROFISSIONAIS -->
        <div class="ficha-section-bar">Informações Profissionais</div>
        <table class="ficha-outer">
          <tr>
            <td colspan="4" class="ficha-row">
              <div>
                <span class="ficha-label">Trabalha atualmente:</span>
                <span
                  v-html="
                    checkboxGroup(
                      ['Sim', 'Não'],
                      printingApplication.form_data['Trabalha atualmente?']
                    )
                  "
                />
              </div>
              <div
                v-if="
                  printingApplication.form_data['Trabalha atualmente?'] &&
                  printingApplication.form_data[
                    'Trabalha atualmente?'
                  ].includes('Sim')
                "
                style="margin-top: 6px"
              >
                <span class="ficha-label">Onde:</span>
                {{ printingApplication.form_data['Empresa atual'] }}
                &nbsp;&nbsp;&nbsp;&nbsp;
                <span class="ficha-label">Função:</span>
                {{ printingApplication.form_data['Função atual'] }}
                &nbsp;&nbsp;&nbsp;&nbsp;
                <span class="ficha-label">Salário:</span> R$
                {{ printingApplication.form_data['Salário atual'] }}
              </div>
            </td>
          </tr>
          <tr>
            <td colspan="4" class="ficha-row">
              <span class="ficha-label"
                >Quais as últimas empresas onde trabalhou:</span
              >
            </td>
          </tr>
          <tr>
            <td class="ficha-row" style="width: 35%">
              <span class="ficha-label">1.</span>
              {{ printingApplication.form_data['Empresa 1'] }}
            </td>
            <td class="ficha-row">
              <span class="ficha-label">Cargo:</span>
              {{ printingApplication.form_data['Cargo 1'] }}
            </td>
            <td class="ficha-row">
              <span class="ficha-label">Duração:</span>
              {{ printingApplication.form_data['Período/duração 1'] }}
            </td>
            <td class="ficha-row" style="width: 12%">
              <span class="ficha-label">Ano:</span>
            </td>
          </tr>
          <tr>
            <td colspan="2" class="ficha-row">
              <span class="ficha-label">Por que saiu?</span>
              {{ printingApplication.form_data['Motivo da saída 1'] }}
            </td>
            <td colspan="2" class="ficha-row">
              <span class="ficha-label">Referência:</span>
              {{
                printingApplication.form_data[
                  'Referência profissional 1 — nome e contato'
                ]
              }}
            </td>
          </tr>
          <tr>
            <td class="ficha-row">
              <span class="ficha-label">2.</span>
              {{ printingApplication.form_data['Empresa 2'] }}
            </td>
            <td class="ficha-row">
              <span class="ficha-label">Cargo:</span>
              {{ printingApplication.form_data['Cargo 2'] }}
            </td>
            <td class="ficha-row">
              <span class="ficha-label">Duração:</span>
              {{ printingApplication.form_data['Período/duração 2'] }}
            </td>
            <td class="ficha-row"><span class="ficha-label">Ano:</span></td>
          </tr>
          <tr>
            <td colspan="2" class="ficha-row">
              <span class="ficha-label">Por que saiu?</span>
              {{ printingApplication.form_data['Motivo da saída 2'] }}
            </td>
            <td colspan="2" class="ficha-row">
              <span class="ficha-label">Referência:</span>
              {{
                printingApplication.form_data[
                  'Referência profissional 2 — nome e contato'
                ]
              }}
            </td>
          </tr>
        </table>

        <!-- ======== PÁGINA 2 ======== -->

        <!-- SOBRE A VAGA -->
        <div class="ficha-section-bar">Sobre a vaga</div>
        <table class="ficha-outer">
          <tr>
            <td class="ficha-row">
              <span class="ficha-label">Como ficou sabendo da vaga?</span>
              {{ printingApplication.form_data['Como ficou sabendo da vaga?'] }}
            </td>
          </tr>
          <tr>
            <td class="ficha-row ficha-multiline">
              <span class="ficha-label">Por que quer trabalhar aqui?</span
              ><br />{{
                printingApplication.form_data[
                  'Por que você quer trabalhar aqui?'
                ]
              }}
            </td>
          </tr>
          <tr>
            <td class="ficha-row ficha-multiline">
              <span class="ficha-label"
                >O que você sabe sobre o Coco Bambu?</span
              ><br />{{
                printingApplication.form_data[
                  'O que você sabe sobre o Coco Bambu?'
                ]
              }}
            </td>
          </tr>
        </table>

        <div
          class="ficha-section-bar"
          style="font-weight: bold; text-decoration: underline"
        >
          Queremos saber um pouco mais sobre você
        </div>
        <table class="ficha-outer">
          <tr>
            <td class="ficha-row ficha-multiline">
              <span class="ficha-label"
                >Qual seu plano para os próximos 2 anos?</span
              ><br />{{
                printingApplication.form_data[
                  'Qual é seu plano para os próximos 2 anos?'
                ]
              }}
            </td>
          </tr>
          <tr>
            <td class="ficha-row ficha-multiline">
              <span class="ficha-label"
                >Qual a sua maior conquista até hoje?</span
              ><br />{{
                printingApplication.form_data[
                  'Qual foi sua maior conquista até hoje?'
                ]
              }}
            </td>
          </tr>
          <tr>
            <td class="ficha-row ficha-multiline">
              <span class="ficha-label">Qual o maior erro que já cometeu?</span
              ><br />{{
                printingApplication.form_data[
                  'Conte um erro que cometeu e o que aprendeu com ele.'
                ]
              }}
            </td>
          </tr>
          <tr>
            <td class="ficha-row ficha-multiline">
              <span class="ficha-label">O que te motiva para o trabalho?</span
              ><br />{{
                printingApplication.form_data['O que motiva você no trabalho?']
              }}
            </td>
          </tr>
          <tr>
            <td class="ficha-row ficha-multiline">
              <span class="ficha-label"
                >Quanto tempo você pretende ficar conosco?</span
              ><br />{{
                printingApplication.form_data[
                  'Quanto tempo você pretende ficar conosco?'
                ]
              }}
            </td>
          </tr>
          <tr>
            <td class="ficha-row ficha-multiline">
              <span class="ficha-label"
                >Fale 3 pontos positivos sobre você?</span
              ><br />{{
                printingApplication.form_data[
                  'Fale 3 pontos positivos sobre você.'
                ]
              }}
            </td>
          </tr>
          <tr>
            <td class="ficha-row ficha-multiline">
              <span class="ficha-label"
                >Fale 3 pontos negativos sobre você?</span
              ><br />{{
                printingApplication.form_data[
                  'Fale 3 pontos que deseja desenvolver.'
                ]
              }}
            </td>
          </tr>
        </table>

        <!-- TERMO -->
        <table class="ficha-outer" style="margin-top: 6px">
          <tr>
            <td
              style="
                font-size: 7px;
                text-align: justify;
                padding: 6px 5px;
                border: 1px solid #999;
                line-height: 1.3;
              "
            >
              No caso de admissão, reconheço que sua decisão de admitir-me foi
              baseada nas informações acima prestadas e que quaisquer erros ou
              omissões aqui contidos darão à empresa o direito de me dispensar
              do meu serviço sem que eu tenha direito a quaisquer indenizações,
              de acordo com o artigo 94 do código civil. Autorizo que a empresa
              utilize meus dados pessoais em outros processos internos,
              inclusive compartilhando com seus parceiros, sendo estas as
              empresas que realizam procedimentos para o regular ingresso do
              colaborador, processamento de banco de dados de candidatos
              reservistas, que aplicam testes de seleção, realizam exames
              admissionais e periódicos, bem como os necessários cadastros para
              acesso aos benefícios e aos sistemas suficientes para desempenho
              das atividades, não havendo limitação aos parceiros acima citados
              e demais fases do processo de seleção, e demais obrigações
              decorrentes do contrato de trabalho.
            </td>
          </tr>
        </table>

        <table
          style="
            width: 100%;
            margin-top: 80px;
            border-collapse: collapse;
            margin-bottom: 12px;
          "
        >
          <tr>
            <td
              class="ficha-center"
              style="width: 40%; border-top: 1px solid black; padding-top: 4px"
            >
              <span class="ficha-label">Data da entrevista</span>
            </td>
            <td style="width: 20%" />
            <td
              class="ficha-center"
              style="width: 40%; border-top: 1px solid black; padding-top: 4px"
            >
              <span class="ficha-label">Assinatura</span>
            </td>
          </tr>
        </table>

        <div class="ficha-page-break" />

        <!-- USO DA EMPRESA -->
        <div class="ficha-section-bar" style="margin-top: 12px">
          NÃO ESCREVER NO ESPAÇO ABAIXO, PARA USO DA EMPRESA
        </div>
        <table class="ficha-outer ficha-bordered">
          <tr>
            <td class="ficha-center ficha-label" style="width: 25%">
              Avaliações
            </td>
            <td class="ficha-center ficha-label" style="width: 15%">Data</td>
            <td class="ficha-center ficha-label" style="width: 10%">Hora</td>
            <td class="ficha-center ficha-label" style="width: 25%">
              Responsável
            </td>
            <td class="ficha-center ficha-label" style="width: 25%">Parecer</td>
          </tr>
          <tr style="height: 16px">
            <td>1ª Entrevista</td>
            <td />
            <td class="ficha-center">:</td>
            <td />
            <td />
          </tr>
          <tr style="height: 16px">
            <td>2ª Entrevista</td>
            <td />
            <td class="ficha-center">:</td>
            <td />
            <td />
          </tr>
          <tr style="height: 16px">
            <td>3ª Entrevista</td>
            <td />
            <td class="ficha-center">:</td>
            <td />
            <td />
          </tr>
        </table>

        <table class="ficha-outer ficha-bordered" style="margin-top: 3px">
          <tr>
            <td class="ficha-center ficha-label" style="width: 50%">
              Pontos positivos (+)
            </td>
            <td class="ficha-center ficha-label" style="width: 50%">
              Pontos Negativos (-)
            </td>
          </tr>
          <tr style="height: 14px">
            <td>-</td>
            <td>-</td>
          </tr>
          <tr style="height: 14px">
            <td>-</td>
            <td>-</td>
          </tr>
          <tr>
            <td><span class="ficha-label">Candidato para cargo:</span></td>
            <td><span class="ficha-label">Setor:</span></td>
          </tr>
          <tr>
            <td>
              <div
                style="
                  display: flex;
                  justify-content: space-between;
                  width: 80%;
                "
              >
                <span><span class="ficha-label">Data de Admissão:</span></span>
                <span><span class="ficha-label">Salário Inicial:</span></span>
              </div>
            </td>
            <td><span class="ficha-label">Aprovado por:</span></td>
          </tr>
          <tr>
            <td
              colspan="2"
              class="ficha-section-bar"
              style="margin: 0; font-size: 9px"
            >
              APROVAÇÃO
            </td>
          </tr>
          <tr style="height: 45px">
            <td
              class="ficha-center ficha-label"
              style="vertical-align: bottom; padding-bottom: 4px"
            >
              Data da aprovação RH
            </td>
            <td
              class="ficha-center ficha-label"
              style="vertical-align: bottom; padding-bottom: 4px"
            >
              Assinatura RH
            </td>
          </tr>
          <tr style="height: 45px">
            <td
              class="ficha-center ficha-label"
              style="vertical-align: bottom; padding-bottom: 4px"
            >
              Data da aprovação FINANCEIRO
            </td>
            <td
              class="ficha-center ficha-label"
              style="vertical-align: bottom; padding-bottom: 4px"
            >
              Assinatura FINANCEIRO
            </td>
          </tr>
          <tr>
            <td class="ficha-center">( &nbsp; ) APROVADO</td>
            <td class="ficha-center">
              ( &nbsp; ) AGUARDAR &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; ( &nbsp;
              ) NÃO APROVADO
            </td>
          </tr>
        </table>
      </div>
    </div>
  </div>
</template>

<style scoped>
.helena-page-bg {
  background: radial-gradient(
      circle at 0% 0%,
      rgba(236, 72, 153, 0.12) 0%,
      transparent 42%
    ),
    radial-gradient(
      circle at 100% 0%,
      rgba(6, 182, 212, 0.12) 0%,
      transparent 40%
    ),
    radial-gradient(
      circle at 100% 100%,
      rgba(79, 70, 229, 0.14) 0%,
      transparent 45%
    ),
    linear-gradient(160deg, #eef2ff 0%, #f8fafc 48%, #e0e7ff 100%);
}
.panel-filters {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  flex: 0 1 auto;
  min-width: 0;
  margin: 0;
}
.panel-filters .panel-filter-input,
.panel-filters .status-select {
  box-sizing: border-box;
  display: inline-block;
  flex: 0 0 auto;
  width: auto;
  max-width: 100%;
  margin: 0;
  height: 2.25rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.5rem;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: #0f172a;
  background-color: #fff;
}
.panel-filters .panel-filter-input:focus,
.panel-filters .status-select:focus {
  outline: none;
  border-color: rgb(99 102 241);
  box-shadow: 0 0 0 1px rgb(99 102 241);
}
.panel-filters .panel-filter-input--date {
  width: 8.75rem;
  min-width: 8.75rem;
  padding: 0.35rem 0.5rem;
}
.panel-filters .panel-filter-input--search {
  width: 13rem;
  min-width: 10rem;
  padding: 0.35rem 0.65rem;
}
.panel-filters .status-select {
  appearance: none;
  -webkit-appearance: none;
  width: 9rem;
  min-width: 9rem;
  padding: 0.35rem 1.75rem 0.35rem 0.65rem;
  font-weight: 500;
  cursor: pointer;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2364748b' d='M2.2 4.2a.75.75 0 0 1 1.06 0L6 6.94l2.74-2.74a.75.75 0 1 1 1.06 1.06l-3.27 3.27a.75.75 0 0 1-1.06 0L2.2 5.26a.75.75 0 0 1 0-1.06z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.55rem center;
  background-size: 0.75rem;
  background-origin: border-box;
}
.panel-filters .status-select:hover {
  border-color: #94a3b8;
}
.status-select {
  appearance: none;
  -webkit-appearance: none;
  display: inline-block;
  width: 9rem;
  margin: 0;
  height: 2.25rem;
  padding: 0.35rem 1.75rem 0.35rem 0.65rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.5rem;
  background-color: #fff;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2364748b' d='M2.2 4.2a.75.75 0 0 1 1.06 0L6 6.94l2.74-2.74a.75.75 0 1 1 1.06 1.06l-3.27 3.27a.75.75 0 0 1-1.06 0L2.2 5.26a.75.75 0 0 1 0-1.06z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.55rem center;
  background-size: 0.75rem;
  color: #0f172a;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
}
.status-select:hover {
  border-color: #94a3b8;
}
.status-select:focus {
  outline: none;
  border-color: rgb(99 102 241);
  box-shadow: 0 0 0 1px rgb(99 102 241);
}
.btn-primary {
  appearance: none;
  border: 0;
  border-radius: 0.5rem;
  min-height: 40px;
  padding: 0 1rem;
  background: rgb(99 102 241);
  color: white;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  transition: background 0.15s ease;
}
.btn-primary:hover {
  background: rgb(79 70 229);
}
</style>

<!-- Unscoped: print must not depend on Tailwind print: variants or scoped attrs. -->
<style>
.recruitment-print-only {
  display: none;
}
@media print {
  body * {
    visibility: hidden !important;
  }
  .recruitment-no-print {
    display: none !important;
  }
  .recruitment-print-only,
  .recruitment-print-only * {
    visibility: visible !important;
  }
  .recruitment-print-only {
    display: block !important;
    position: absolute !important;
    left: 0 !important;
    top: 0 !important;
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
    color: #000 !important;
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 9px !important;
    line-height: 1.3 !important;
  }
  .recruitment-print-only .ficha-outer {
    width: 100% !important;
    border-collapse: collapse !important;
    table-layout: fixed !important;
    margin: 0 0 4px !important;
    border: 1px solid #888 !important;
  }
  .recruitment-print-only .ficha-outer td {
    padding: 2px 4px !important;
    vertical-align: top !important;
  }
  .recruitment-print-only .ficha-section-bar {
    background-color: #6366f1 !important;
    color: #fff !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    text-align: center !important;
    font-weight: bold !important;
    font-size: 10px !important;
    padding: 2px 0 !important;
    border: 1px solid #888 !important;
    margin: 0 !important;
  }
  .recruitment-print-only .ficha-row {
    border-bottom: 1px solid #ccc !important;
  }
  .recruitment-print-only .ficha-multiline {
    border-bottom: 1px solid #ccc !important;
    min-height: 22px !important;
    padding-bottom: 6px !important;
  }
  .recruitment-print-only .ficha-label {
    font-weight: bold !important;
  }
  .recruitment-print-only .ficha-center {
    text-align: center !important;
  }
  .recruitment-print-only .ficha-logo-cell {
    width: 15% !important;
    text-align: center !important;
    vertical-align: middle !important;
    border-right: 1px solid #888 !important;
  }
  .recruitment-print-only .ficha-title-cell {
    width: 60% !important;
    text-align: center !important;
    font-size: 14px !important;
    font-weight: bold !important;
    vertical-align: middle !important;
  }
  .recruitment-print-only .ficha-num-cell {
    width: 25% !important;
    font-size: 11px !important;
    vertical-align: middle !important;
    border-left: 1px solid #888 !important;
    padding-left: 8px !important;
  }
  .recruitment-print-only .ficha-page-break {
    break-before: page !important;
    page-break-before: always !important;
  }
  .recruitment-print-only .ficha-bordered td {
    border: 1px solid #888 !important;
    padding: 3px 5px !important;
  }
}
</style>
